import * as mapbox from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import MapboxDirections from "@mapbox/mapbox-gl-directions/dist/mapbox-gl-directions";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";

// VARIABLES GLOBALES
let map;
let directions = null;
let currentProfile = "mapbox/driving-traffic";
let userLocation = null;
let destinationLocation = null;
let hasRealUserGeolocation = false;
let buttonSetupDone = false; // Bandera para evitar duplicar event listeners
let geocoder = null;
let userManuallySelectedLocation = false; // Bandera para recordar si el usuario seleccionó una dirección manualmente

// Reverse geocode to get a human-friendly place name for coordinates
async function reverseGeocode(lng, lat) {
    if (!accessToken) return null;
    try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${encodeURIComponent(accessToken)}&types=place,locality,neighborhood,address&limit=1&country=es,pt,fr`;
        const res = await fetch(url);
        if (!res.ok) return null;
        const data = await res.json();
        const place = data?.features?.[0];
        return place?.place_name || null;
    } catch (e) {
        return null;
    }
}

const accessToken = document.querySelector("#tokenmapboxcomollegar")?.value;
console.log("Token Mapbox cargado:", accessToken);

// ACTUALIZA ESTILOS DEL PERFIL ACTIVO
function updateButtonStyles(profile) {
    const wrappers = {
        walking: document.querySelector('.walking-wrapper'),
        cycling: document.querySelector('.cycling-wrapper'),
        driving: document.querySelector('.driving-wrapper'),
    };
    const labels = {
        walking: document.querySelector('label[for="walking-profile"]'),
        cycling: document.querySelector('label[for="cycling-profile"]'),
        driving: document.querySelector('label[for="driving-profile"]'),
    };

    // remove active from wrappers
    Object.values(wrappers).forEach(w => w?.classList.remove("active"));
    Object.values(labels).forEach(l => l && (l.style.borderBottom = "2px solid transparent"));

    if (profile === "mapbox/walking") {
        wrappers.walking?.classList.add("active");
        // if (labels.walking) labels.walking.style.borderBottom = "2px solid #2a6e78";
    } else if (profile === "mapbox/cycling") {
        wrappers.cycling?.classList.add("active");
        // if (labels.cycling) labels.cycling.style.borderBottom = "2px solid #2a6e78";
    } else {
        wrappers.driving?.classList.add("active");
        // if (labels.driving) labels.driving.style.borderBottom = "2px solid #2a6e78";
    }
}

// INICIALIZA DIRECTIONS - IMPORTANTE: Recrea el mapa
function initializeDirections(profile) {
    //console.log("initializeDirections() - perfil:", profile);

    // Eliminar Directions anterior si existe
    if (directions) {
        try {
            map.removeControl(directions);

        } catch (e) {
            //console.warn("Error eliminando Directions:", e);
        }
        directions = null;
    }

    // Crear nuevo control Directions
    directions = new MapboxDirections({
        unit: "metric",
        profile: profile,
        interactive: false,
        controls: { inputs: false, instructions: false },
        accessToken: accessToken,
    });

    map.addControl(directions, "top-left");

    // Escuchar cuando se calcula la ruta
    directions.on("route", (e) => {
        if (e.route && e.route.length > 0) {
            showRouteDuration(e.route[0]);
        }
    });

    // (Re)initialize Geocoder and attach to the DOM container (clean container first)
    if (geocoder == null) {
        console.log("Inicializando Geocoder...");
        try {
            const geocoderContainer = document.getElementById('geocoder');
            if (geocoderContainer) {
                // remove any existing geocoder DOM inside the container to avoid duplicates
                const existing = geocoderContainer.querySelectorAll('.mapboxgl-ctrl-geocoder');
                existing.forEach(el => el.remove());
            }

            geocoder = new MapboxGeocoder({
                accessToken,
                mapboxgl: mapbox,
                placeholder: document.querySelector(".map-responsive")?.dataset?.placeholder || 'Introduce origen...',
                marker: false,
                countries: 'es,pt,fr',
                types: 'place,locality,neighborhood,address'
            });

            if (geocoderContainer) {
                geocoder.addTo(geocoderContainer);
            } else {
                geocoder.addTo(map);
            }

            geocoder.on('result', (e) => {
                if (e && e.result && e.result.center) {
                    userLocation = e.result.center; // [lng, lat]
                    userManuallySelectedLocation = true; // El usuario seleccionó manualmente una dirección
                    setupRoute();
                }
            });
        } catch (err) {
            console.warn('Geocoder init failed', err);
        }
    }

    updateButtonStyles(profile);
}

// MUESTRA DURACIÓN Y DISTANCIA DE LA RUTA
function showRouteDuration(route) {
    if (!route) return;

    const distance = route.distance; // en metros
    const duration = route.duration; // en segundos

    // Convertir duración a horas y minutos
    const hours = Math.floor(duration / 3600);
    const minutes = Math.ceil((duration % 3600) / 60);
    let durationText = "";
    if (hours > 0) {
        durationText = `${hours}h${minutes}min`;
    } else {
        durationText = `${minutes}min`;
    }

    // Convertir distancia a km
    const distanceKm = (distance / 1000).toFixed(1);

    // Buscar o crear el elemento de duración
    let durationElement = document.getElementById("route-duration-tooltip");
    if (!durationElement) {
        durationElement = document.createElement("div");
        durationElement.id = "route-duration-tooltip";
        durationElement.style.cssText = `
            position: absolute;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: #0ea5e9;
            color: white;
            padding: 8px 16px;
            border-radius: 8px;
            font-weight: bold;
            font-size: 14px;
            z-index: 100;
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
            white-space: nowrap;
            pointer-events: none;
        `;
        const container = document.querySelector(".map-responsive");
        if (container) {
            container.style.position = "relative";
            container.appendChild(durationElement);
        }
    }

    durationElement.textContent = `${durationText} - ${distanceKm}km`;
}

// CONFIGURA LOS BOTONES / INPUTS DE PERFIL
function setupProfileButtons() {
    // Solo configurar una vez
    if (buttonSetupDone) {
        updateButtonStyles(currentProfile);
        return;
    }

    const walkingLabel = document.querySelector('label[for="walking-profile"]');
    const cyclingLabel = document.querySelector('label[for="cycling-profile"]');
    const drivingLabel = document.querySelector('label[for="driving-profile"]');
    const walkingInput = document.getElementById('walking-profile');
    const cyclingInput = document.getElementById('cycling-profile');
    const drivingInput = document.getElementById('driving-profile');

    if (!walkingInput || !cyclingInput || !drivingInput || !walkingLabel || !cyclingLabel || !drivingLabel) return;

    const handleProfileChange = (profile, input) => {
        currentProfile = profile;
        input.checked = true;
        setupProfileButtons();
        updateButtonStyles(profile);

        // Siempre reinicializar el mapa completo - mismo flujo para todos los casos
        initializeMap();
    };

    // Usar click en labels (funciona incluso si ya está seleccionado)
    walkingLabel.addEventListener('click', (e) => {
        e.preventDefault();
        handleProfileChange("mapbox/walking", walkingInput);
    });

    cyclingLabel.addEventListener('click', (e) => {
        e.preventDefault();
        handleProfileChange("mapbox/cycling", cyclingInput);
    });

    drivingLabel.addEventListener('click', (e) => {
        e.preventDefault();
        handleProfileChange("mapbox/driving-traffic", drivingInput);
    });

    // Inicializar por defecto
    drivingInput.checked = true;
    updateButtonStyles(currentProfile);

    // Marcar como hecho para no hacerlo de nuevo
    buttonSetupDone = true;
}

// SETUP ROUTE - Establece origen y destino
function setupRoute() {
    if (!map || !userLocation || !destinationLocation) return;

    if (directions && typeof directions.setOrigin === 'function' && typeof directions.setDestination === 'function') {
        try {
            directions.setOrigin(userLocation);
            directions.setDestination(destinationLocation);
            fitRouteBounds();
        } catch (e) {
            console.warn("Error setting route:", e);
            // Reintentar después de un poco más de tiempo
            setTimeout(() => {
                if (directions && typeof directions.setOrigin === 'function') {
                    try {
                        directions.setOrigin(userLocation);
                        directions.setDestination(destinationLocation);
                        fitRouteBounds();
                    } catch (e2) {
                        console.warn("Error en reintento de ruta:", e2);
                    }
                }
            }, 300);
        }
    }
}

// INICIALIZAR MAPA
function initializeMap() {
    //console.log("initializeMap()");

    const container = document.querySelector(".map-responsive");
    if (!container) return;

    const lat = parseFloat(container.dataset.lat);
    const lng = parseFloat(container.dataset.lng);
    destinationLocation = [lng, lat];

    // Remover mapa anterior si existe
    if (map) {
        map.remove();
        map = null;
    }

    mapbox.accessToken = accessToken;
    map = new mapbox.Map({
        container,
        style: "mapbox://styles/mapbox/streets-v12",
        center: [lng, lat],
        zoom: 12,
    });

    map.on("load", () => {
        //console.log("Mapa cargado");
        initializeDirections(currentProfile);
        setupProfileButtons();
        getUserLocation();
    });
}

// GEOLOCALIZAR USUARIO
function getUserLocation() {
    //console.log("getUserLocation()");
    
    // Si el usuario ya seleccionó una dirección manualmente, no geolocalizar de nuevo
    if (userManuallySelectedLocation) {
        hasRealUserGeolocation = true;
        setupRoute();
        return;
    }

    if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                userLocation = [pos.coords.longitude, pos.coords.latitude];
                hasRealUserGeolocation = true;

                // Try to get a prettier place name for the input via reverse geocoding
                (async () => {
                    let label = null;
                    try {
                        label = await reverseGeocode(userLocation[0], userLocation[1]);
                    } catch (e) {
                        label = null;
                    }

                    if (!label) label = 'Current location';

                    try {
                        if (geocoder && typeof geocoder.setInput === 'function') {
                            geocoder.setInput(label);
                        } else if (geocoder && typeof geocoder.query === 'function') {
                            geocoder.query(label);
                        }
                    } catch (e) {
                        // ignore
                    }

                    setupRoute();
                })();
            },
            (err) => {
                //console.warn("Geolocalización denegada, usando destino");
                userLocation = destinationLocation;
                hasRealUserGeolocation = false;
                setupRoute();
            }
        );
    } else {
        //console.warn("Geolocalización no soportada, usando destino");
        userLocation = destinationLocation;
        hasRealUserGeolocation = false;
        setupRoute();
    }
}

// AJUSTAR EL MAPA A ORIGEN + DESTINO
function fitRouteBounds() {
    if (!map || !userLocation || !destinationLocation) return;

    // Si no hay geolocalización real, evitar que fitBounds haga zoom extremo en una ruta de 0 m.
    if (!hasRealUserGeolocation) {
        map.easeTo({
            center: destinationLocation,
            zoom: 11,
            duration: 500,
        });
        return;
    }

    const bounds = new mapbox.LngLatBounds()
        .extend(userLocation)
        .extend(destinationLocation);

    map.fitBounds(bounds, { padding: 100 });
}

// INICIALIZAR AL CARGAR DOM
document.addEventListener("DOMContentLoaded", initializeMap);
